import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { normalizeContentfulAssetUrl } from '@sg/garnish';

import { useContentfulContentTypeEntry } from '@order/Contentful';

import type { Asset } from 'contentful';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the Contentful entry backing the bundle screen, falling back to
 * bundled copy for any text the entry leaves out. Banners fall back where
 * they're shown, since their copy needs the add to bag results.
 */
export const useBundleContent = () => {
  const { formatMessage } = useIntl();

  const { data, fetching } = useContentfulContentTypeEntry<BundleFields>({
    contentType: BUNDLE_CONTENT_TYPE,
  });

  const fields = data?.fields;

  // ─── Derived ─────────────────────────────────────────────────────────

  const content = useMemo<BundleContent>(() => {
    const imageUrl = fields?.image?.fields.file?.url;

    return {
      name: fields?.name ?? formatMessage(messages.name),
      description: fields?.description ?? formatMessage(messages.description),
      selectTitle: fields?.selectTitle ?? formatMessage(messages.selectTitle),
      lineageKeys: fields?.productsLineageKeys ?? [],
      successBanner: fields?.successBanner,
      partialSuccessBanner: fields?.partialSuccessBanner,
      failureBanner: fields?.failureBanner,
      imageUrl: imageUrl ? normalizeContentfulAssetUrl(imageUrl) : undefined,
      imageA11y: fields?.image?.fields.description,
    };
  }, [fields, formatMessage]);

  return { content, fetching };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const BUNDLE_CONTENT_TYPE = 'productsBundle';

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  name: {
    defaultMessage: 'Family Meal',
    description: 'Bundle | Fallback content | Bundle name',
  },
  description: {
    defaultMessage: 'Choose four of our hearty plates. Customize them after you add to your bag.',
    description: 'Bundle | Fallback content | Bundle description',
  },
  selectTitle: {
    defaultMessage: 'Select a plate',
    description: 'Bundle | Fallback content | Select a product CTA title',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type BundleContent = {
  name: string;
  description: string;
  selectTitle: string;
  lineageKeys: string[];
  successBanner?: string;
  partialSuccessBanner?: string;
  failureBanner?: string;
  imageUrl?: string;
  imageA11y?: string;
};

type BundleFields = {
  name?: string;
  description?: string;
  selectTitle?: string;
  productsLineageKeys?: string[];
  successBanner?: string;
  partialSuccessBanner?: string;
  failureBanner?: string;
  image?: Asset;
};
